import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Reusable pagination controls.
 * Props:
 *   page        - current page (1-based)
 *   pageSize    - items per page
 *   total       - total item count
 *   onPage      - (newPage) => void
 *   onPageSize  - (newSize) => void
 *   pageSizes   - optional array of sizes, default [25, 50, 100]
 */
export function PageControls({ page, pageSize, total, onPage, onPageSize, pageSizes = [25, 50, 100] }) {
  const totalPages = Math.ceil(total / pageSize) || 1;
  const from = Math.min((page - 1) * pageSize + 1, total);
  const to = Math.min(page * pageSize, total);

  return (
    <div className="flex items-center justify-between flex-wrap gap-3 py-3 px-1">
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Show</span>
        <Select value={String(pageSize)} onValueChange={v => { onPageSize(Number(v)); onPage(1); }}>
          <SelectTrigger className="h-7 w-20 text-xs rounded-lg">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizes.map(s => (
              <SelectItem key={s} value={String(s)} className="text-xs">{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">per page</span>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground tabular-nums">
          {total > 0 ? `${from}–${to} of ${total}` : '0 results'}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="outline" size="icon"
            className="h-7 w-7 rounded-lg"
            onClick={() => onPage(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft size={14} />
          </Button>
          <span className="text-xs tabular-nums px-1">{page} / {totalPages}</span>
          <Button
            variant="outline" size="icon"
            className="h-7 w-7 rounded-lg"
            onClick={() => onPage(page + 1)}
            disabled={page >= totalPages}
          >
            <ChevronRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}
